// Small SEO helper — sets per-page <title>, meta description, canonical
// URL and OG tags on mount, restores them on unmount. Zero dependencies.
// Used by service pages so /services/:slug renders unique metadata and
// ?focus= query parameters canonicalise back to the clean service URL.

import { useEffect } from "react";

const DEFAULT_TITLE = "Ur Designs";
const DEFAULT_DESC = "Ur Designs — creative services for brands in Aruba and beyond.";
// Prerender (SEO Phase 2) injects window.__SEO_ORIGIN__ so canonicals/OG
// URLs in static snapshots carry the real public origin instead of the
// local prerender server host. Normal browsing is unaffected.
const SITE_ORIGIN = typeof window !== "undefined" ? (window.__SEO_ORIGIN__ || window.location.origin) : "";

function upsertMeta(nameOrProp, key, value) {
  if (typeof document === "undefined") return null;
  let el = document.querySelector(`meta[${nameOrProp}="${key}"]`);
  const existed = !!el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(nameOrProp, key);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", value);
  return { el, prev, existed };
}

function upsertCanonical(url) {
  if (typeof document === "undefined") return null;
  let el = document.querySelector('link[rel="canonical"]');
  const existed = !!el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", url);
  return { el, prev, existed };
}

// Trim a plain-text description to a search-friendly length without
// mid-word cuts.
function clampDescription(text, max = 160) {
  const t = (text || "").replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const idx = cut.lastIndexOf(" ");
  return (idx > 40 ? cut.slice(0, idx) : cut).replace(/[,;:.\s]+$/, "") + "…";
}

export function usePageSeo({ title, description, path, image, noindex } = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const prevTitle = document.title;
    if (title) document.title = title;
    const finalDesc = clampDescription(description || DEFAULT_DESC);
    const metaDesc  = upsertMeta("name", "description", finalDesc);
    const canonical = path ? upsertCanonical(`${SITE_ORIGIN}${path}`) : null;
    const ogTitle   = upsertMeta("property", "og:title", title || DEFAULT_TITLE);
    const ogDesc    = upsertMeta("property", "og:description", finalDesc);
    const ogType    = upsertMeta("property", "og:type", "website");
    const ogUrl     = path ? upsertMeta("property", "og:url", `${SITE_ORIGIN}${path}`) : null;
    // og:image must be absolute for crawlers — host-relative CMS asset
    // paths are resolved against the public origin.
    const absImage  = image && image.startsWith("/") ? `${SITE_ORIGIN}${image}` : image;
    const ogImage   = absImage ? upsertMeta("property", "og:image", absImage) : null;
    const twCard    = upsertMeta("name", "twitter:card", absImage ? "summary_large_image" : "summary");
    const robots    = noindex ? upsertMeta("name", "robots", "noindex, nofollow") : null;
    return () => {
      document.title = prevTitle;
      const restore = (m) => {
        if (!m) return;
        if (m.prev === null || m.prev === undefined) {
          if (!m.existed && m.el?.parentNode) m.el.parentNode.removeChild(m.el);
        } else {
          m.el.setAttribute("content", m.prev);
        }
      };
      restore(metaDesc); restore(ogTitle); restore(ogDesc); restore(ogType); restore(ogUrl); restore(ogImage); restore(twCard); restore(robots);
      if (canonical) {
        if (canonical.prev === null || canonical.prev === undefined) {
          if (!canonical.existed && canonical.el?.parentNode) canonical.el.parentNode.removeChild(canonical.el);
        } else {
          canonical.el.setAttribute("href", canonical.prev);
        }
      }
    };
  }, [title, description, path, image, noindex]);
}

// Minimal robots noindex for internal/transactional routes (checkout,
// admin, estimate results). Paired with Disallow rules in robots.txt.
export function useNoIndex() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const m = upsertMeta("name", "robots", "noindex, nofollow");
    return () => {
      if (!m) return;
      if (m.prev === null || m.prev === undefined) {
        if (!m.existed && m.el?.parentNode) m.el.parentNode.removeChild(m.el);
      } else {
        m.el.setAttribute("content", m.prev);
      }
    };
  }, []);
}
